import { EmptyState } from '@/components/common/EmptyState';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ImagePreviewDialog } from '@/components/dialogs/ImagePreviewDialog';
import { TextViewDialog } from '@/components/dialogs/TextViewDialog';
import { aptGenerationApi } from '@/api/apt_generation.api';
import { detectionsApi } from '@/api/detections.api';
import { useDetections } from '@/hooks/useDetections';
import { useHoles } from '@/hooks/useHoles';
import type { HoleObjectModel } from '@/types/apt_generation.types';
import { ORIENTATION_ORDER } from '@/types/detection.types';
import type { ProcedureModel } from '@/types/procedure.types';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  GripVertical,
  Layers,
  Pencil,
  Sparkles,
  X,
} from 'lucide-react';
import { Button } from 'primereact/button';
import { TabPanel, TabView } from 'primereact/tabview';
import type { FC } from 'react';
import { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface APTGenerationPanelProps {
  projectId: string;
  /** When provided, only extractions for this model are listed. The panel lists
   *  extractions with extraction_type "holes" only (other types are hidden). */
  targetModelId?: string | null;
  /** Called when APT Generation panel is past loading (task completed); false while loading. */
  onAptGenerationReady?: (ready: boolean) => void;
}

const getRegenerateStorageKey = (projectId: string) => `regenerateTask_${projectId}_target`;

/**
 * Group procedures by (procedure_name, tool_number, tool_diameter, total_depth, mo_type, spindle_speed_rpm)
 * so that only procedures that share all these attributes are merged. Collect associated holes per group.
 */
/** True when full hole list was fetched for this extraction (including empty list). */
function hasLoadedHolesList(holesData: Record<string, HoleObjectModel[]>, extractionId: string): boolean {
  return Object.prototype.hasOwnProperty.call(holesData, extractionId);
}

function buildProcedureGroups(
  holes: HoleObjectModel[]
): { key: string; procedure: ProcedureModel; holes: HoleObjectModel[] }[] {
  const map = new Map<string, { procedure: ProcedureModel; holes: HoleObjectModel[] }>();
  for (const hole of holes) {
    for (const proc of hole.procedures ?? []) {
      const key = [
        proc.procedure_name ?? '',
        proc.assembly.tool?.tool_number_unique ?? '',
        proc.assembly.tool?.tool_diameter ?? '',
        proc.total_depth,
        proc.mo_type,
        proc.spindle_speed_rpm,
      ].join('|');
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { procedure: proc, holes: [hole] });
      } else {
        existing.holes.push(hole);
      }
    }
  }
  return Array.from(map.entries(), ([key, { procedure, holes }]) => ({ key, procedure, holes }));
}

export const APTGenerationPanel: FC<APTGenerationPanelProps> = ({ projectId, targetModelId, onAptGenerationReady }) => {
  const { t } = useTranslation();
  const {
    detections,
    isLoading: isLoadingDetections,
    error: detectionsError,
    refetch: refetchDetections,
  } = useDetections(projectId);
  const { fetchHoles, isLoading: isLoadingHoles } = useHoles();
  const [expandedHoles, setExpandedHoles] = useState<Set<string>>(new Set());
  const [holesData, setHolesData] = useState<Record<string, HoleObjectModel[]>>({});
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [previewHoleId, setPreviewHoleId] = useState<string | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewProcedureId, setPreviewProcedureId] = useState<string | null>(null);
  const [previewThumbnails, setPreviewThumbnails] = useState<Record<string, string>>({});
  const [previewProcedureThumbnails, setPreviewProcedureThumbnails] = useState<Record<string, string>>({});
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [showAPTSourceDialog, setShowAPTSourceDialog] = useState(false);
  const [aptSourceContent, setAptSourceContent] = useState<string>('');
  const [aptSourceHoleId, setAptSourceHoleId] = useState<string | null>(null);
  const [aptSourceFilename, setAptSourceFilename] = useState<string | null>(null);
  const [holesCounts, setHolesCounts] = useState<Record<string, number>>({});
  const [currentPage, setCurrentPage] = useState<Record<string, number>>({});
  const [viewMode, setViewMode] = useState<'holes' | 'procedures'>('holes');
  const [expandedProcedures, setExpandedProcedures] = useState<Set<string>>(new Set());
  const [expandedProcedureHolesPage, setExpandedProcedureHolesPage] = useState<Record<string, number>>({});
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [editingProcedureId, setEditingProcedureId] = useState<string | null>(null);
  const [editingProcedureName, setEditingProcedureName] = useState('');
  /** Extraction id for which a swap-order request is in progress; disables drag handle. */
  const [swappingExtractionId, setSwappingExtractionId] = useState<string | null>(null);
  const HOLES_PER_PAGE = 6;
  const PROCEDURES_PER_PAGE = 6;
  const EXPANDED_HOLES_PER_PAGE = 6;
  const wasRegeneratingRef = useRef(false);
  const refetchDetectionsRef = useRef(refetchDetections);
  refetchDetectionsRef.current = refetchDetections;

  // Check if regeneration is in progress and handle state changes
  useEffect(() => {
    const storageKey = getRegenerateStorageKey(projectId);
    const checkRegeneration = () => {
      const taskId = localStorage.getItem(storageKey);
      const currentlyRegenerating = !!taskId;

      // If regeneration just started, clear all holes data and cache
      if (currentlyRegenerating && !wasRegeneratingRef.current) {
        setHolesData({});
        setExpandedHoles(new Set());
        setHolesCounts({});
        setPreviewThumbnails({});
        setPreviewProcedureThumbnails({});

        // Clear cache
        try {
          localStorage.removeItem(`holes_counts_${projectId}`);
          // Clear blob cache for this project
          import('@/utils/blobCache').then(({ clearProjectCache }) => {
            clearProjectCache(projectId).catch(() => {
              // Ignore errors
            });
          });
        } catch (_error) {
          // Ignore
        }
      }

      // If regeneration just finished, refetch detections
      if (!currentlyRegenerating && wasRegeneratingRef.current) {
        refetchDetectionsRef.current();
      }

      setIsRegenerating(currentlyRegenerating);
      wasRegeneratingRef.current = currentlyRegenerating;
    };

    // Check on mount
    const initialTaskId = localStorage.getItem(storageKey);
    wasRegeneratingRef.current = !!initialTaskId;
    setIsRegenerating(!!initialTaskId);
    if (initialTaskId) {
      // If regeneration is already in progress, clear holes data
      setHolesData({});
      setExpandedHoles(new Set());
    }

    // Listen for storage changes (when regeneration starts/ends)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey) {
        checkRegeneration();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also poll localStorage periodically (for same-tab changes)
    const interval = setInterval(checkRegeneration, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [projectId]);

  /** Extractions shown in this panel: same model (when targetModelId set) and APT/holes type only. */
  const aptPanelDetections = useMemo(() => {
    const byModel = targetModelId ? detections.filter((d) => d.model_id === targetModelId) : detections;
    return byModel.filter((d) => d.extraction_type === 'holes');
  }, [detections, targetModelId]);

  const sortedDetections = useMemo(() => {
    return [...aptPanelDetections].sort((a, b) => {
      const aIndex = ORIENTATION_ORDER.indexOf(a.orientation);
      const bIndex = ORIENTATION_ORDER.indexOf(b.orientation);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [aptPanelDetections]);

  useEffect(() => {
    setActiveTabIndex((idx) => {
      if (sortedDetections.length === 0) return 0;
      return idx >= sortedDetections.length ? 0 : idx;
    });
  }, [sortedDetections]);

  const currentExtractionId = useMemo(() => {
    if (sortedDetections.length > 0 && activeTabIndex >= 0 && activeTabIndex < sortedDetections.length) {
      return sortedDetections[activeTabIndex].id;
    }
    return null;
  }, [sortedDetections, activeTabIndex]);

  // Fetch holes when tab changes or extraction changes
  useEffect(() => {
    if (!currentExtractionId) {
      return;
    }

    // Fetch holes if not already loaded
    if (!holesData[currentExtractionId]) {
      fetchHoles(currentExtractionId).then(async (allHoles) => {
        const holes = allHoles.filter((h) => h.object_type !== 'Chamfer');
        setHolesData((prev) => ({ ...prev, [currentExtractionId]: holes }));

        // Load preview thumbnails for holes and procedures (with cache)
        const { getCachedBlob, setCachedBlob } = await import('@/utils/blobCache');
        const holeThumbnailPromises = holes.map(async (hole) => {
          const cacheKey = `png_${hole.id}`;
          const cachedBlob = await getCachedBlob(cacheKey);
          if (cachedBlob) {
            return { holeId: hole.id, imageUrl: URL.createObjectURL(cachedBlob) };
          }
          try {
            const blob = await aptGenerationApi.getHolePng(hole.id);
            await setCachedBlob(cacheKey, blob);
            return { holeId: hole.id, imageUrl: URL.createObjectURL(blob) };
          } catch {
            return null;
          }
        });

        const procedureIdsWithPng = new Set<string>();
        for (const hole of holes) {
          for (const proc of hole.procedures ?? []) {
            if (proc.png_s3_url) procedureIdsWithPng.add(proc.id);
          }
        }
        const procedureThumbnailPromises = Array.from(procedureIdsWithPng).map(async (procedureId) => {
          const cacheKey = `procedure_png_${procedureId}`;
          const cachedBlob = await getCachedBlob(cacheKey);
          if (cachedBlob) {
            return { procedureId, imageUrl: URL.createObjectURL(cachedBlob) };
          }
          try {
            const blob = await aptGenerationApi.getProcedurePng(procedureId);
            await setCachedBlob(cacheKey, blob);
            return { procedureId, imageUrl: URL.createObjectURL(blob) };
          } catch {
            return null;
          }
        });

        Promise.all([Promise.all(holeThumbnailPromises), Promise.all(procedureThumbnailPromises)]).then(
          ([holeResults, procedureResults]) => {
            const newThumbnails: Record<string, string> = {};
            for (const result of holeResults) {
              if (result) newThumbnails[result.holeId] = result.imageUrl;
            }
            setPreviewThumbnails((prev) => ({ ...prev, ...newThumbnails }));
            const newProcedureThumbnails: Record<string, string> = {};
            for (const result of procedureResults) {
              if (result) newProcedureThumbnails[result.procedureId] = result.imageUrl;
            }
            setPreviewProcedureThumbnails((prev) => ({ ...prev, ...newProcedureThumbnails }));
          }
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentExtractionId, fetchHoles]);

  // Tab badge counts: use len(holesData[id]) when full list is loaded; else holes-count API (cached).
  useEffect(() => {
    let cancelled = false;

    const fetchHolesCounts = async () => {
      const cacheKey = `holes_counts_${projectId}`;
      const cacheExpiry = 5 * 60 * 1000; // 5 minutes

      const detectionIdList = aptPanelDetections.map((d) => d.id);
      const currentDetectionIds = new Set(detectionIdList);

      const countsFromLoadedHoles: Record<string, number> = {};
      for (const id of detectionIdList) {
        if (hasLoadedHolesList(holesData, id)) {
          countsFromLoadedHoles[id] = holesData[id].length;
        }
      }

      const idsNeedingApiCount = detectionIdList.filter((id) => !hasLoadedHolesList(holesData, id));

      const mergeAndSet = (apiOrCachedCounts: Record<string, number>, persistToCache: boolean) => {
        const merged: Record<string, number> = { ...countsFromLoadedHoles };
        for (const id of detectionIdList) {
          if (merged[id] !== undefined) {
            continue;
          }
          if (apiOrCachedCounts[id] !== undefined) {
            merged[id] = apiOrCachedCounts[id];
          }
        }
        if (cancelled) {
          return;
        }
        setHolesCounts(merged);
        if (persistToCache && detectionIdList.length > 0) {
          try {
            localStorage.setItem(
              cacheKey,
              JSON.stringify({
                counts: merged,
                timestamp: Date.now(),
                detectionIds: detectionIdList,
              })
            );
          } catch (_error) {
            // localStorage might be full or disabled, continue anyway
          }
        }
      };

      if (detectionIdList.length === 0) {
        return;
      }

      if (idsNeedingApiCount.length === 0) {
        mergeAndSet({}, true);
        return;
      }

      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { counts, timestamp, detectionIds } = JSON.parse(cached) as {
            counts: Record<string, number>;
            timestamp: number;
            detectionIds: string[];
          };
          const now = Date.now();
          const cachedIds = new Set(detectionIds || []);
          const idsMatch =
            currentDetectionIds.size === cachedIds.size &&
            Array.from(currentDetectionIds).every((id) => cachedIds.has(id));

          if (now - timestamp < cacheExpiry && idsMatch) {
            const validCounts: Record<string, number> = {};
            for (const id of idsNeedingApiCount) {
              if (counts[id] !== undefined) {
                validCounts[id] = counts[id];
              }
            }
            const stillMissing = idsNeedingApiCount.filter((id) => validCounts[id] === undefined);
            if (stillMissing.length === 0) {
              mergeAndSet(validCounts, false);
              return;
            }
            const missingPromises = stillMissing.map(async (id) => {
              try {
                const holesCount = await detectionsApi.getHolesCount(id);
                return { id, count: holesCount };
              } catch (_error) {
                return { id, count: 0 };
              }
            });
            const missingResults = await Promise.all(missingPromises);
            for (const result of missingResults) {
              validCounts[result.id] = result.count;
            }
            mergeAndSet(validCounts, true);
            return;
          }
        }
      } catch (_error) {
        // Cache invalid or doesn't exist, continue to fetch
      }

      const countPromises = idsNeedingApiCount.map(async (id) => {
        try {
          const holesCount = await detectionsApi.getHolesCount(id);
          return { id, count: holesCount };
        } catch (_error) {
          return { id, count: 0 };
        }
      });
      const results = await Promise.all(countPromises);
      const fetched: Record<string, number> = {};
      for (const result of results) {
        fetched[result.id] = result.count;
      }
      mergeAndSet(fetched, true);
    };

    fetchHolesCounts().catch(() => {
      /* best-effort; errors already handled per request inside fetchHolesCounts */
    });
    return () => {
      cancelled = true;
    };
  }, [aptPanelDetections, projectId, holesData]);

  const handleViewProcedureAPTSource = async (e: React.MouseEvent, procedureId: string) => {
    e.stopPropagation();
    setAptSourceHoleId(procedureId); // reuse for dialog title; could be procedure id
    setAptSourceFilename(null);
    try {
      const blob = await aptGenerationApi.downloadProcedureAPTSource(procedureId);
      const text = await blob.text();
      setAptSourceContent(text);
      setShowAPTSourceDialog(true);
    } catch (_error) {
      toast.error(t('projectDetail.previewError') || 'Failed to load APT source file');
    }
  };

  const handleViewProcedureHFile = async (e: React.MouseEvent, procedureId: string, filename: string) => {
    e.stopPropagation();
    setAptSourceHoleId(procedureId);
    setAptSourceFilename(filename);
    try {
      const blob = await aptGenerationApi.getProcedureHFile(procedureId, filename);
      const text = await blob.text();
      setAptSourceContent(text);
      setShowAPTSourceDialog(true);
    } catch (_error) {
      toast.error(t('projectDetail.previewError') || 'Failed to load file');
    }
  };

  const handleDownloadProcedureHFilesZip = async (e: React.MouseEvent, procedureId: string) => {
    e.stopPropagation();
    try {
      const response = await aptGenerationApi.downloadProcedureHFilesZip(procedureId);
      let filename = `h-files_${procedureId}.zip`;
      const contentDisposition = response.headers?.['content-disposition'];
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match?.[1]) {
          filename = match[1].trim();
        }
      }
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_error) {
      toast.error(t('projectDetail.downloadError') || 'Failed to download H files zip');
    }
  };

  const handleViewPreview = async (e: React.MouseEvent, hole: HoleObjectModel) => {
    e.stopPropagation(); // Prevent row expansion when clicking preview button
    setPreviewHoleId(hole.id);

    // Use thumbnail if available, otherwise load full image (with cache)
    if (previewThumbnails[hole.id]) {
      setPreviewImageUrl(previewThumbnails[hole.id]);
    } else {
      try {
        const { getCachedBlob, setCachedBlob } = await import('@/utils/blobCache');
        const cacheKey = `png_${hole.id}`;

        // Try cache first
        let blob = await getCachedBlob(cacheKey);
        if (!blob) {
          // Not in cache, fetch from API
          blob = await aptGenerationApi.getHolePng(hole.id);
          // Cache the blob
          await setCachedBlob(cacheKey, blob);
        }

        const imageUrl = URL.createObjectURL(blob);
        setPreviewImageUrl(imageUrl);
        setPreviewThumbnails((prev) => ({ ...prev, [hole.id]: imageUrl }));
      } catch (_error) {
        setPreviewImageUrl(null);
        toast.error(t('projectDetail.previewError') || 'Failed to load preview');
      }
    }
  };

  const handleClosePreview = () => {
    setPreviewHoleId(null);
    setPreviewImageUrl(null);
    setPreviewProcedureId(null);
  };

  const handleViewProcedurePreview = (e: React.MouseEvent, procedureId: string) => {
    e.stopPropagation();
    setPreviewProcedureId(procedureId);
  };

  const handleSaveProcedureName = async (extractionId: string, procedureId: string, newName: string) => {
    const trimmed = newName.trim();
    try {
      await aptGenerationApi.updateProcedure(procedureId, {
        procedure_name: trimmed || null,
      });
      setHolesData((prev) => {
        const holes = prev[extractionId];
        if (!holes) return prev;
        return {
          ...prev,
          [extractionId]: holes.map((hole) => ({
            ...hole,
            procedures: (hole.procedures ?? []).map((p) =>
              p.id === procedureId ? { ...p, procedure_name: trimmed || null } : p
            ),
          })),
        };
      });
      setEditingProcedureId(null);
      setEditingProcedureName('');
      toast.success(t('common.success') || 'Saved');
    } catch {
      toast.error(t('common.error') || 'Failed to update procedure name');
    }
  };

  const handleSwapProcedures = async (extractionId: string, procedureIdA: string, procedureIdB: string) => {
    if (procedureIdA === procedureIdB) return;
    setSwappingExtractionId(extractionId);
    try {
      await aptGenerationApi.swapProcedureOrder(procedureIdA, procedureIdB);
      const allHoles = await fetchHoles(extractionId);
      const holes = allHoles.filter((h) => h.object_type !== 'Chamfer');
      setHolesData((prev) => ({ ...prev, [extractionId]: holes }));
      toast.success(t('projectDetail.procedureOrderSwapped') ?? 'Pořadí prohozeno');
    } catch {
      toast.error(t('projectDetail.procedureOrderSwapError') ?? 'Nepodařilo prohodit pořadí');
    } finally {
      setSwappingExtractionId(null);
    }
  };

  const handleDownloadAptSourcesZip = async () => {
    setIsDownloadingZip(true);
    try {
      // Always download project-level procedures ZIP
      const response = await aptGenerationApi.downloadAptSourcesZipProject(projectId);

      // Extract filename from Content-Disposition header or use fallback
      let filename = `apt_sources_project_${projectId}.zip`;
      const contentDisposition = response.headers?.['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1];
        }
      }

      const blob = response.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_error) {
      toast.error(t('projectDetail.downloadError') || 'Failed to download APT source archive');
    } finally {
      setIsDownloadingZip(false);
    }
  };

  // Cleanup preview thumbnails on unmount
  useEffect(() => {
    return () => {
      setPreviewThumbnails((current) => {
        Object.values(current).forEach((url) => URL.revokeObjectURL(url));
        return {};
      });
      setPreviewProcedureThumbnails((current) => {
        Object.values(current).forEach((url) => URL.revokeObjectURL(url));
        return {};
      });
      if (previewImageUrl && previewHoleId && previewThumbnails[previewHoleId] !== previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
    };
  }, []);

  // View mode toggle (holes/procedures) – shown when panel is visible
  const isToggleAvailable = !isRegenerating;

  // Notify parent when APT Generation is ready: detections loaded, no regen, and active tab content (holes) loaded
  const activeTabContentLoaded = !currentExtractionId || holesData[currentExtractionId] !== undefined;
  const aptGenerationReady = !isLoadingDetections && !isRegenerating && activeTabContentLoaded;
  useEffect(() => {
    onAptGenerationReady?.(aptGenerationReady);
    return () => {
      onAptGenerationReady?.(false);
    };
  }, [aptGenerationReady, onAptGenerationReady]);

  // Show loading when loading detections or regenerating
  if (isLoadingDetections || isRegenerating) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Sparkles size={24} className="text-primary" />
            <h2 className="text-xl font-semibold text-gray-900">{t('projectDetail.aptGeneration')}</h2>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <LoadingSpinner size="large" />
          {isRegenerating && <p className="text-gray-500 text-center">{t('projectDetail.regeneratingAffectsAPT')}</p>}
        </div>
      </div>
    );
  }

  if (detectionsError) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Sparkles size={24} className="text-primary" />
            <h2 className="text-xl font-semibold text-gray-900">{t('projectDetail.aptGeneration')}</h2>
          </div>
        </div>
        <ErrorMessage message={detectionsError} />
      </div>
    );
  }

  if (sortedDetections.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Sparkles size={24} className="text-primary" />
            <h2 className="text-xl font-semibold text-gray-900">{t('projectDetail.aptGeneration')}</h2>
          </div>
        </div>
        <EmptyState title={t('projectDetail.aptGeneration')} message={t('projectDetail.aptGenerationArea')} />
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Sparkles size={24} className="text-primary" />
          <h2 className="text-xl font-semibold text-gray-900">{t('projectDetail.aptGeneration')}</h2>
        </div>
      </div>

      {/* Extractions as tabs – one outer border around tab strip and table */}
      {sortedDetections.length > 0 && (
        <div className="rounded-lg border border-gray-200 overflow-hidden [&_.p-tabview-panels]:p-0 [&_.p-tabview-panel]:p-0 [&_.p-tabview-nav]:mb-0 [&_.p-tabview-nav]:border-0 [&_.p-tabview-nav]:border-b-0 [&_.p-tabview-nav]:rounded-t-none [&_.p-tabview-nav]:bg-transparent [&_.p-tabview-nav-container]:border-b [&_.p-tabview-nav-container]:border-gray-200 [&_.p-tabview-nav-container]:pr-40 relative">
          <TabView
            activeIndex={activeTabIndex}
            onTabChange={(e) => {
              setActiveTabIndex(e.index);
              const extractionId = sortedDetections[e.index]?.id;
              if (extractionId) {
                setCurrentPage((prev) => ({ ...prev, [extractionId]: 0 }));
              }
            }}
          >
            {sortedDetections.map((extraction) => {
              const holes = holesData[extraction.id] || [];
              const sortedHoles = [...holes].sort((a, b) => a.order_number - b.order_number);
              const isLoadingHolesForExtraction = !holesData[extraction.id] && isLoadingHoles;
              const tabHolesCount = hasLoadedHolesList(holesData, extraction.id)
                ? holesData[extraction.id].length
                : holesCounts[extraction.id] !== undefined
                  ? holesCounts[extraction.id]
                  : '...';
              const procedureGroups = [...buildProcedureGroups(holes)].sort(
                (a, b) => a.procedure.order_number - b.procedure.order_number
              );

              // Pagination: holes view vs procedures view
              const page = currentPage[extraction.id] || 0;
              const totalPagesHoles = Math.ceil(sortedHoles.length / HOLES_PER_PAGE);
              const totalPagesProcedures = Math.ceil(procedureGroups.length / PROCEDURES_PER_PAGE);
              const totalPages = viewMode === 'procedures' ? totalPagesProcedures : totalPagesHoles;
              const startIndex = page * (viewMode === 'procedures' ? PROCEDURES_PER_PAGE : HOLES_PER_PAGE);
              const endIndex = startIndex + (viewMode === 'procedures' ? PROCEDURES_PER_PAGE : HOLES_PER_PAGE);
              const paginatedHoles = sortedHoles.slice(
                viewMode === 'holes' ? startIndex : 0,
                viewMode === 'holes' ? endIndex : sortedHoles.length
              );
              const paginatedProcedureGroups = procedureGroups.slice(
                viewMode === 'procedures' ? startIndex : 0,
                viewMode === 'procedures' ? endIndex : procedureGroups.length
              );

              // Pad with empty rows for consistent table height
              const rowsPerPage = viewMode === 'procedures' ? PROCEDURES_PER_PAGE : HOLES_PER_PAGE;
              const emptyRowsCount =
                rowsPerPage - (viewMode === 'procedures' ? paginatedProcedureGroups.length : paginatedHoles.length);
              const emptyRows = Array.from({ length: Math.max(0, emptyRowsCount) }, (_, i) => ({
                id: `empty-${i}`,
                isEmpty: true,
              }));

              const handlePageChange = (newPage: number) => {
                if (newPage >= 0 && newPage < totalPages) {
                  setCurrentPage((prev) => ({ ...prev, [extraction.id]: newPage }));
                }
              };

              return (
                <TabPanel
                  key={extraction.id}
                  header={
                    <div className="flex items-center gap-2">
                      <span>{extraction.orientation}</span>
                      <span className="text-xs text-gray-500">({tabHolesCount})</span>
                    </div>
                  }
                >
                  {isLoadingHolesForExtraction ? (
                    <div className="flex justify-center py-8">
                      <LoadingSpinner size="medium" />
                    </div>
                  ) : sortedHoles.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">{t('projectDetail.noHolesFound')}</div>
                  ) : viewMode === 'procedures' ? (
                    /* Procedure perspective: # leftmost, then Procedure specs (9 cols, last is placeholder), then Hole specs (3 cols) right; vertical line left of Hole specs */
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[64rem] table-fixed border-collapse text-sm">
                          <colgroup>
                            <col className="w-10" />
                            <col className="w-14" />
                            <col className="w-36" />
                            <col className="w-20" />
                            <col className="w-24" />
                            <col className="w-24" />
                            <col className="w-22" />
                            <col className="w-22" />
                            <col className="w-12" />
                            <col className="w-24" />
                            <col className="w-12" />
                            <col className="w-16" />
                            <col className="w-16" />
                            <col className="w-16" />
                          </colgroup>
                          <thead>
                            <tr className="bg-gray-50/80 border-t border-b border-gray-300">
                              <th
                                className="text-left py-2.5 px-4 font-medium text-gray-600 w-10"
                                aria-label={t('projectDetail.reorderProcedures') ?? 'Změna pořadí'}
                              />
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600" />
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600" colSpan={9}>
                                {t('projectDetail.procedureSpecifications')}
                              </th>
                              <th
                                className="text-left py-2.5 px-4 font-medium text-gray-600 border-l border-gray-300"
                                colSpan={3}
                              >
                                {t('projectDetail.holeSpecifications')}
                              </th>
                            </tr>
                            <tr className="bg-gray-50/50 border-b border-gray-200">
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600 w-10" />
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600 whitespace-nowrap">#</th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.procedureNameLine1')}
                                <br />
                                {t('projectDetail.procedureNameLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.toolNumberLine1')}
                                <br />
                                {t('projectDetail.toolNumberLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.toolDiameterLine1')}
                                <br />
                                {t('projectDetail.toolDiameterLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.totalDepthLine1')}
                                <br />
                                {t('projectDetail.totalDepthLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.moTypeLine1')}
                                <br />
                                {t('projectDetail.moTypeLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.spindleSpeedLine1')}
                                <br />
                                {t('projectDetail.spindleSpeedLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.holesCountLine1')}
                                <br />
                                {t('projectDetail.holesCountLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600 whitespace-nowrap">
                                {t('projectDetail.preview')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600 w-12" aria-hidden />
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600 border-l border-gray-300 whitespace-nowrap">
                                {t('projectDetail.xMm')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600 whitespace-nowrap">
                                {t('projectDetail.yMm')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.diameterMmLine1')}
                                <br />
                                {t('projectDetail.diameterMmLine2')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedProcedureGroups.map((group) => {
                              const isExpanded = expandedProcedures.has(group.key);
                              const hasHoles = group.holes.length > 0;
                              const isSwapping = swappingExtractionId === extraction.id;
                              return (
                                <>
                                  <tr
                                    key={group.key}
                                    className={`border-b border-gray-300 transition-colors ${
                                      hasHoles ? 'cursor-pointer hover:bg-gray-50/70' : ''
                                    }`}
                                    title={hasHoles ? t('projectDetail.clickToExpandHoles') || undefined : undefined}
                                    onClick={() => {
                                      if (hasHoles) {
                                        setExpandedProcedures((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(group.key)) next.delete(group.key);
                                          else next.add(group.key);
                                          return next;
                                        });
                                        setExpandedProcedureHolesPage((prev) => {
                                          const next = { ...prev };
                                          delete next[group.key];
                                          return next;
                                        });
                                      }
                                    }}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'move';
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const draggedId = e.dataTransfer.getData('text/plain');
                                      if (draggedId && draggedId !== group.procedure.id) {
                                        handleSwapProcedures(extraction.id, draggedId, group.procedure.id);
                                      }
                                    }}
                                  >
                                    <td
                                      className="py-2 px-2 text-gray-400 w-10 align-middle"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <span
                                        draggable={!isSwapping}
                                        onDragStart={(e) => {
                                          e.stopPropagation();
                                          e.dataTransfer.setData('text/plain', group.procedure.id);
                                          e.dataTransfer.effectAllowed = 'move';
                                        }}
                                        className={`inline-flex cursor-grab active:cursor-grabbing touch-none ${isSwapping ? 'pointer-events-none opacity-50' : 'hover:text-gray-600'}`}
                                        title={
                                          t('projectDetail.dragToReorderProcedures') ?? 'Přetáhni pro prohození pořadí'
                                        }
                                        aria-label={t('projectDetail.dragToReorderProcedures') ?? 'Změna pořadí řádku'}
                                      >
                                        <GripVertical size={16} aria-hidden />
                                      </span>
                                    </td>
                                    <td className="py-2 px-4 text-sm text-gray-900 w-14">
                                      <div className="flex items-center gap-1.5">
                                        {hasHoles ? (
                                          isExpanded ? (
                                            <ChevronDown size={14} className="text-gray-400 shrink-0" aria-hidden />
                                          ) : (
                                            <ChevronRight size={14} className="text-gray-400 shrink-0" aria-hidden />
                                          )
                                        ) : (
                                          <span className="w-3.5 shrink-0" aria-hidden />
                                        )}
                                        <span className="tabular-nums">{group.procedure.order_number}</span>
                                      </div>
                                    </td>
                                    <td
                                      className="py-2 px-4 text-gray-700 break-words hyphens-auto min-w-0 overflow-hidden"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {editingProcedureId === group.procedure.id ? (
                                        <div className="flex items-center gap-1">
                                          <input
                                            type="text"
                                            value={editingProcedureName}
                                            onChange={(e) => setEditingProcedureName(e.target.value)}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                handleSaveProcedureName(
                                                  extraction.id,
                                                  group.procedure.id,
                                                  editingProcedureName
                                                );
                                              }
                                              if (e.key === 'Escape') {
                                                setEditingProcedureId(null);
                                                setEditingProcedureName('');
                                              }
                                            }}
                                            className="min-w-[8rem] rounded border border-gray-300 px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                            autoFocus
                                            aria-label={t('projectDetail.procedureName')}
                                          />
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleSaveProcedureName(
                                                extraction.id,
                                                group.procedure.id,
                                                editingProcedureName
                                              )
                                            }
                                            className="p-1 text-green-600 hover:bg-green-50 hover:text-green-700 rounded transition-colors"
                                            title={t('common.save')}
                                            aria-label={t('common.save')}
                                          >
                                            <Check size={14} />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setEditingProcedureId(null);
                                              setEditingProcedureName('');
                                            }}
                                            className="p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 rounded transition-colors"
                                            title={t('common.cancel')}
                                            aria-label={t('common.cancel')}
                                          >
                                            <X size={14} />
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="flex items-center gap-2 min-w-0">
                                          <span className="min-w-0 break-words hyphens-auto">
                                            {group.procedure.procedure_name ?? group.procedure.assembly.assembly_id}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEditingProcedureId(group.procedure.id);
                                              setEditingProcedureName(
                                                group.procedure.procedure_name ??
                                                  group.procedure.assembly.assembly_id ??
                                                  ''
                                              );
                                            }}
                                            className="p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 rounded transition-colors"
                                            title={t('projectDetail.editProcedureName')}
                                            aria-label={t('projectDetail.editProcedureName')}
                                          >
                                            <Pencil size={14} />
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                    <td className="py-2 px-4 text-gray-700 tabular-nums">
                                      {group.procedure.assembly.tool?.tool_number_unique}
                                    </td>
                                    <td className="py-2 px-4 text-gray-700 tabular-nums">
                                      {group.procedure.assembly.tool?.tool_diameter != null
                                        ? group.procedure.assembly.tool.tool_diameter.toFixed(2)
                                        : ''}
                                    </td>
                                    <td className="py-2 px-4 text-gray-700 tabular-nums">
                                      {group.procedure.total_depth.toFixed(2)}
                                    </td>
                                    <td className="py-2 px-4 text-gray-700 break-words hyphens-auto">
                                      {group.procedure.mo_type}
                                    </td>
                                    <td className="py-2 px-4 text-gray-700 tabular-nums">
                                      {group.procedure.spindle_speed_rpm.toFixed(0)}
                                    </td>
                                    <td className="py-2 px-4 text-gray-700 tabular-nums">{group.holes.length}</td>
                                    <td className="py-2 px-4">
                                      <div className="flex items-center gap-2">
                                        <span
                                          className="cursor-pointer hover:bg-gray-50/60 transition-colors rounded inline-flex shrink-0"
                                          role="button"
                                          tabIndex={0}
                                          title={t('projectDetail.view3DPreview') || 'Zobrazit 3D náhled'}
                                          aria-label={t('projectDetail.view3DPreview') || 'Zobrazit 3D náhled'}
                                          onClick={(e) => handleViewProcedurePreview(e, group.procedure.id)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleViewProcedurePreview(
                                                e as unknown as React.MouseEvent,
                                                group.procedure.id
                                              );
                                            }
                                          }}
                                        >
                                          {previewProcedureThumbnails[group.procedure.id] ? (
                                            <img
                                              src={previewProcedureThumbnails[group.procedure.id]}
                                              alt=""
                                              className="w-20 h-20 object-contain rounded pointer-events-none"
                                              onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                              }}
                                            />
                                          ) : (
                                            <span
                                              className="inline-flex w-20 h-20 items-center justify-center text-gray-400 text-xs rounded bg-gray-50/50"
                                              title={
                                                group.procedure.apt_s3_url
                                                  ? t('projectDetail.viewAPTSource') || 'View APT source'
                                                  : t('projectDetail.view3DPreview') || 'Zobrazit 3D náhled'
                                              }
                                              aria-hidden
                                            />
                                          )}
                                        </span>
                                        {group.procedure.apt_s3_url && (
                                          <button
                                            type="button"
                                            onClick={(e) => handleViewProcedureAPTSource(e, group.procedure.id)}
                                            className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors shrink-0"
                                            title={t('projectDetail.viewAPTSource') || 'View APT source'}
                                            aria-label={t('projectDetail.viewAPTSource') || 'View APT source'}
                                          >
                                            <FileText size={14} />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                    <td className="py-2 px-4 text-gray-400 w-12" />
                                    <td className="py-2 px-4 text-gray-400 border-l border-gray-300" />
                                    <td className="py-2 px-4 text-gray-400" />
                                    <td className="py-2 px-4 text-gray-400" />
                                  </tr>
                                  {isExpanded &&
                                    (() => {
                                      const holesPage = expandedProcedureHolesPage[group.key] ?? 0;
                                      const paginatedExpandedHoles = group.holes.slice(
                                        holesPage * EXPANDED_HOLES_PER_PAGE,
                                        (holesPage + 1) * EXPANDED_HOLES_PER_PAGE
                                      );
                                      const totalExpandedPages = Math.ceil(
                                        group.holes.length / EXPANDED_HOLES_PER_PAGE
                                      );
                                      const hasExpandedPagination = group.holes.length > EXPANDED_HOLES_PER_PAGE;
                                      const hFilesList = group.procedure.h_files ?? [];
                                      const hasHFilesContent = hFilesList.length > 0 || !!group.procedure.h_files_pplog;
                                      const lastHolesPage = totalExpandedPages - 1;
                                      const showHFilesHeaderRow = hasHFilesContent && holesPage === 0;
                                      const pairHFileWithHoleColumn = hasHFilesContent && holesPage === 0;
                                      return (
                                        <>
                                          {showHFilesHeaderRow && (
                                            <tr
                                              key={`${group.key}-h-files-header`}
                                              className="border-b border-gray-300 bg-blue-50/70 text-gray-800"
                                            >
                                              <td
                                                className="py-1.5 px-4 align-middle border-r border-gray-200"
                                                colSpan={11}
                                                onClick={(ev) => ev.stopPropagation()}
                                              >
                                                <div className="flex items-center gap-2 min-w-0 w-full pl-6 pr-6">
                                                  <span className="font-medium text-gray-600 shrink-0">
                                                    {t('projectDetail.hFilesSectionLabel')}
                                                  </span>
                                                  <div className="flex items-center gap-1 ml-auto flex-wrap justify-end">
                                                    {group.procedure.h_files_pplog && (
                                                      <button
                                                        type="button"
                                                        onClick={(e) =>
                                                          handleViewProcedureHFile(
                                                            e,
                                                            group.procedure.id,
                                                            group.procedure.h_files_pplog!
                                                          )
                                                        }
                                                        className="flex items-center gap-1.5 px-2 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors shrink-0 text-sm min-w-0"
                                                        title={t('projectDetail.viewPplogFile')}
                                                        aria-label={t('projectDetail.viewPplogFile')}
                                                      >
                                                        <FileText size={16} className="shrink-0" />
                                                        <span className="break-all hyphens-auto min-w-0 truncate">
                                                          {group.procedure.h_files_pplog}
                                                        </span>
                                                      </button>
                                                    )}
                                                    <button
                                                      type="button"
                                                      onClick={(e) =>
                                                        handleDownloadProcedureHFilesZip(e, group.procedure.id)
                                                      }
                                                      className="flex items-center gap-1.5 px-2 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors shrink-0 text-sm"
                                                      title={t('projectDetail.downloadHFilesZipTooltip')}
                                                      aria-label={t('projectDetail.downloadHFilesZipTooltip')}
                                                    >
                                                      <Download size={16} />
                                                      <span>{t('projectDetail.downloadHFilesZipButton')}</span>
                                                    </button>
                                                  </div>
                                                </div>
                                              </td>
                                              <td
                                                className="py-1.5 px-4 border-l border-gray-200 bg-gray-50/50"
                                                colSpan={3}
                                                aria-hidden
                                              />
                                            </tr>
                                          )}
                                          {paginatedExpandedHoles.map((hole, rowIndex) => {
                                            const globalHoleIndex = holesPage * EXPANDED_HOLES_PER_PAGE + rowIndex;
                                            const hFileForRow = pairHFileWithHoleColumn
                                              ? hFilesList[globalHoleIndex]
                                              : undefined;
                                            return (
                                              <tr
                                                key={hole.id}
                                                className="border-b border-gray-300 bg-gray-50/50 hover:bg-gray-50 transition-colors"
                                              >
                                                <td
                                                  className="py-1.5 px-4 align-middle border-r border-gray-200 text-gray-700"
                                                  colSpan={11}
                                                  onClick={(ev) => ev.stopPropagation()}
                                                >
                                                  {hFileForRow ? (
                                                    <div className="flex items-center gap-2 min-w-0 pl-6 pr-6">
                                                      <span className="break-all hyphens-auto min-w-0">
                                                        {hFileForRow}
                                                      </span>
                                                      <button
                                                        type="button"
                                                        onClick={(e) =>
                                                          handleViewProcedureHFile(e, group.procedure.id, hFileForRow)
                                                        }
                                                        className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors shrink-0"
                                                        title={t('projectDetail.viewHFile')}
                                                        aria-label={t('projectDetail.viewHFile')}
                                                      >
                                                        <FileText size={16} />
                                                      </button>
                                                    </div>
                                                  ) : null}
                                                </td>
                                                <td className="py-1.5 px-4 text-gray-700 tabular-nums border-l border-gray-200 align-middle">
                                                  {hole.x_mm.toFixed(2)}
                                                </td>
                                                <td className="py-1.5 px-4 text-gray-700 tabular-nums align-middle">
                                                  {hole.y_mm.toFixed(2)}
                                                </td>
                                                <td className="py-1.5 px-4 text-gray-700 tabular-nums align-middle">
                                                  {hole.diameter_mm.toFixed(2)}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                          {holesPage === lastHolesPage &&
                                            pairHFileWithHoleColumn &&
                                            hFilesList.length > group.holes.length &&
                                            hFilesList.slice(group.holes.length).map((hFileName, idx) => (
                                              <tr
                                                key={`${group.key}-h-file-only-${idx}`}
                                                className="border-b border-gray-300 bg-gray-50/50 hover:bg-gray-50 transition-colors"
                                              >
                                                <td
                                                  className="py-1.5 px-4 align-middle border-r border-gray-200 text-gray-700"
                                                  colSpan={11}
                                                  onClick={(ev) => ev.stopPropagation()}
                                                >
                                                  <div className="flex items-center gap-2 min-w-0 pl-6 pr-6">
                                                    <span className="break-all hyphens-auto min-w-0">{hFileName}</span>
                                                    <button
                                                      type="button"
                                                      onClick={(e) =>
                                                        handleViewProcedureHFile(e, group.procedure.id, hFileName)
                                                      }
                                                      className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors shrink-0"
                                                      title={t('projectDetail.viewHFile')}
                                                      aria-label={t('projectDetail.viewHFile')}
                                                    >
                                                      <FileText size={16} />
                                                    </button>
                                                  </div>
                                                </td>
                                                <td
                                                  className="py-1.5 px-4 border-l border-gray-200 bg-gray-50/40"
                                                  colSpan={3}
                                                  aria-hidden
                                                />
                                              </tr>
                                            ))}
                                          {hasExpandedPagination && (
                                            <tr className="border-b border-gray-300 bg-gray-50/30">
                                              <td className="py-1.5 px-4" colSpan={11} />
                                              <td className="py-1.5 px-4 border-l border-gray-200" colSpan={3}>
                                                <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
                                                  <span>
                                                    {t('common.showing')} {holesPage * EXPANDED_HOLES_PER_PAGE + 1}–
                                                    {Math.min(
                                                      (holesPage + 1) * EXPANDED_HOLES_PER_PAGE,
                                                      group.holes.length
                                                    )}{' '}
                                                    {t('common.of')} {group.holes.length}
                                                  </span>
                                                  <div className="flex items-center gap-1">
                                                    <button
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedProcedureHolesPage((prev) => ({
                                                          ...prev,
                                                          [group.key]: Math.max(0, (prev[group.key] ?? 0) - 1),
                                                        }));
                                                      }}
                                                      disabled={holesPage === 0}
                                                      className="px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                                      aria-label={t('common.previousPage')}
                                                    >
                                                      <ChevronLeft size={14} />
                                                    </button>
                                                    <button
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedProcedureHolesPage((prev) => ({
                                                          ...prev,
                                                          [group.key]: Math.min(
                                                            totalExpandedPages - 1,
                                                            (prev[group.key] ?? 0) + 1
                                                          ),
                                                        }));
                                                      }}
                                                      disabled={holesPage >= totalExpandedPages - 1}
                                                      className="px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                                      aria-label={t('common.nextPage')}
                                                    >
                                                      <ChevronRight size={14} />
                                                    </button>
                                                  </div>
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                        </>
                                      );
                                    })()}
                                </>
                              );
                            })}
                            {emptyRows.map((emptyRow) => (
                              <tr key={emptyRow.id} className="border-b border-gray-300 bg-gray-50/20">
                                <td className="py-2 px-4 text-gray-400 w-10" />
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400">
                                  <span className="inline-block w-20 h-20" />
                                </td>
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400 border-l border-gray-300" />
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400" />
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {totalPages > 1 && (
                        <div className="flex justify-between mt-4 px-4">
                          <div className="text-sm text-gray-600">
                            {t('common.showing')} {startIndex + 1} - {Math.min(endIndex, procedureGroups.length)}{' '}
                            {t('common.of')} {procedureGroups.length}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handlePageChange(page - 1)}
                              disabled={page === 0}
                              className={`px-3 py-1 text-sm rounded border ${page === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                              aria-label={t('common.previousPage')}
                            >
                              <ChevronLeft size={16} className="inline" />
                            </button>
                            <div className="flex items-center gap-1">
                              {Array.from({ length: totalPages }, (_, i) => i).map((pageNum) => {
                                const showPage =
                                  pageNum === 0 ||
                                  pageNum === totalPages - 1 ||
                                  (pageNum >= page - 1 && pageNum <= page + 1);
                                if (!showPage && pageNum === 1 && page > 2)
                                  return (
                                    <span key={pageNum} className="px-2 text-gray-400">
                                      ...
                                    </span>
                                  );
                                if (!showPage && pageNum === totalPages - 2 && page < totalPages - 3)
                                  return (
                                    <span key={pageNum} className="px-2 text-gray-400">
                                      ...
                                    </span>
                                  );
                                if (!showPage) return null;
                                return (
                                  <button
                                    key={pageNum}
                                    type="button"
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`px-3 py-1 text-sm rounded border ${pageNum === page ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                                    aria-label={`${t('common.page')} ${pageNum + 1}`}
                                  >
                                    {pageNum + 1}
                                  </button>
                                );
                              })}
                            </div>
                            <button
                              type="button"
                              onClick={() => handlePageChange(page + 1)}
                              disabled={page >= totalPages - 1}
                              className={`px-3 py-1 text-sm rounded border ${page >= totalPages - 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                              aria-label={t('common.nextPage')}
                            >
                              <ChevronRight size={16} className="inline" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[64rem] table-fixed border-collapse text-sm">
                          <colgroup>
                            <col className="w-14" />
                            <col className="w-20" />
                            <col className="w-20" />
                            <col className="w-20" />
                            <col className="w-24" />
                            <col className="w-28" />
                            <col className="w-36" />
                            <col className="w-20" />
                            <col className="w-24" />
                            <col className="w-24" />
                            <col className="w-22" />
                            <col className="w-22" />
                          </colgroup>
                          <thead>
                            <tr className="bg-gray-50/80 border-t border-b border-gray-300">
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600" colSpan={6}>
                                {t('projectDetail.holeSpecifications')}
                              </th>
                              <th
                                className="text-left py-2.5 px-4 font-medium text-gray-600 border-l border-gray-300"
                                colSpan={6}
                              >
                                {t('projectDetail.procedureSpecifications')}
                              </th>
                            </tr>
                            <tr className="bg-gray-50/50 border-b border-gray-200">
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600 whitespace-nowrap">#</th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600 whitespace-nowrap">
                                {t('projectDetail.xMm')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600 whitespace-nowrap">
                                {t('projectDetail.yMm')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.diameterMmLine1')}
                                <br />
                                {t('projectDetail.diameterMmLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600 whitespace-nowrap">
                                {t('projectDetail.preview')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600 min-w-0 overflow-hidden break-words hyphens-auto">
                                {t('aptSources.procedureCountLine1')}
                                <br />
                                {t('aptSources.procedureCountLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600 border-l border-gray-300">
                                {t('projectDetail.procedureNameLine1')}
                                <br />
                                {t('projectDetail.procedureNameLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.toolNumberLine1')}
                                <br />
                                {t('projectDetail.toolNumberLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.toolDiameterLine1')}
                                <br />
                                {t('projectDetail.toolDiameterLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.totalDepthLine1')}
                                <br />
                                {t('projectDetail.totalDepthLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.moTypeLine1')}
                                <br />
                                {t('projectDetail.moTypeLine2')}
                              </th>
                              <th className="text-left py-2.5 px-4 font-medium text-gray-600">
                                {t('projectDetail.spindleSpeedLine1')}
                                <br />
                                {t('projectDetail.spindleSpeedLine2')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedHoles.map((hole) => {
                              const isHoleExpanded = expandedHoles.has(hole.id);
                              const hasProcedures = hole.procedures && hole.procedures.length > 0;
                              const hasAptError = !hasProcedures && Boolean(hole.apt_error);

                              return (
                                <Fragment key={hole.id}>
                                  <tr
                                    className={`border-b border-gray-300 transition-colors ${
                                      hasProcedures ? 'cursor-pointer hover:bg-gray-50/70' : ''
                                    } ${hasAptError ? 'bg-red-50' : ''}`}
                                    title={
                                      hasProcedures
                                        ? t('projectDetail.clickToExpandProcedures') || undefined
                                        : undefined
                                    }
                                    onClick={() => {
                                      if (hasProcedures) {
                                        setExpandedHoles((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(hole.id)) {
                                            next.delete(hole.id);
                                          } else {
                                            next.add(hole.id);
                                          }
                                          return next;
                                        });
                                      }
                                    }}
                                  >
                                    <td className="py-2 px-4 text-sm text-gray-900 w-14">
                                      <div className="flex items-center gap-1.5">
                                        {hasProcedures ? (
                                          isHoleExpanded ? (
                                            <ChevronDown size={14} className="text-gray-400 shrink-0" aria-hidden />
                                          ) : (
                                            <ChevronRight size={14} className="text-gray-400 shrink-0" aria-hidden />
                                          )
                                        ) : hasAptError ? (
                                          <AlertTriangle size={14} className="text-red-500 shrink-0" aria-hidden />
                                        ) : (
                                          <span className="w-3.5 shrink-0" aria-hidden />
                                        )}
                                        <span className="tabular-nums">{hole.order_number}</span>
                                      </div>
                                    </td>
                                    <td className="py-2 px-4 text-gray-700 tabular-nums">{hole.x_mm.toFixed(2)}</td>
                                    <td className="py-2 px-4 text-gray-700 tabular-nums">{hole.y_mm.toFixed(2)}</td>
                                    <td className="py-2 px-4 text-gray-700 tabular-nums">
                                      {hole.diameter_mm.toFixed(2)}
                                    </td>
                                    <td
                                      className="py-2 px-4 cursor-pointer hover:bg-gray-50/60 transition-colors"
                                      role="button"
                                      tabIndex={0}
                                      title={t('projectDetail.viewPreview') || 'View preview'}
                                      aria-label={t('projectDetail.viewPreview') || 'View preview'}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewPreview(e, hole);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleViewPreview(
                                            e as unknown as React.MouseEvent<HTMLTableCellElement>,
                                            hole
                                          );
                                        }
                                      }}
                                    >
                                      {previewThumbnails[hole.id] ? (
                                        <img
                                          src={previewThumbnails[hole.id]}
                                          alt=""
                                          className="w-20 h-20 object-contain pointer-events-none rounded"
                                          onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                          }}
                                        />
                                      ) : (
                                        <span
                                          className="inline-flex w-20 h-20 items-center justify-center text-gray-400 text-xs rounded bg-gray-50/50"
                                          aria-hidden
                                        />
                                      )}
                                    </td>
                                    <td className="py-2 px-4 text-gray-700 tabular-nums">
                                      {hole.procedures?.length ?? 0}
                                    </td>
                                    <td
                                      className="py-2 px-4 bg-gray-50/30 border-l border-gray-200/80 align-top"
                                      colSpan={6}
                                      aria-hidden
                                    />
                                  </tr>
                                  {hasAptError && (
                                    <tr className="border-b border-gray-300 bg-red-50">
                                      <td colSpan={12} className="py-1.5 px-4 pl-9">
                                        <div className="flex items-start gap-2 text-xs text-red-700">
                                          <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                                          <span className="break-all">{hole.apt_error}</span>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                  {/* Procedures rows – no preview in Holes mode (preview only for holes); first row tighter to parent; sorted by order_number */}
                                  {isHoleExpanded &&
                                    hasProcedures &&
                                    [...(hole.procedures ?? [])]
                                      .sort((a, b) => a.order_number - b.order_number)
                                      .map((procedure, procedureIndex) => (
                                        <tr
                                          key={procedure.id}
                                          className={`border-b border-gray-300 bg-gray-50/50 hover:bg-gray-50 transition-colors ${procedureIndex === 0 ? 'pt-0' : ''}`}
                                        >
                                          <td className={`px-4 ${procedureIndex === 0 ? 'pt-0.5 pb-1.5' : 'py-1.5'}`} />
                                          <td className={`px-4 ${procedureIndex === 0 ? 'pt-0.5 pb-1.5' : 'py-1.5'}`} />
                                          <td className={`px-4 ${procedureIndex === 0 ? 'pt-0.5 pb-1.5' : 'py-1.5'}`} />
                                          <td className={`px-4 ${procedureIndex === 0 ? 'pt-0.5 pb-1.5' : 'py-1.5'}`} />
                                          <td className={`px-4 ${procedureIndex === 0 ? 'pt-0.5 pb-1.5' : 'py-1.5'}`} />
                                          <td className={`px-4 ${procedureIndex === 0 ? 'pt-0.5 pb-1.5' : 'py-1.5'}`} />
                                          <td
                                            className={`px-4 text-gray-700 border-l border-gray-200 break-words hyphens-auto min-w-0 overflow-hidden ${procedureIndex === 0 ? 'pt-0.5 pb-1.5' : 'py-1.5'}`}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {editingProcedureId === procedure.id ? (
                                              <div className="flex items-center gap-1">
                                                <input
                                                  type="text"
                                                  value={editingProcedureName}
                                                  onChange={(e) => setEditingProcedureName(e.target.value)}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                      handleSaveProcedureName(
                                                        extraction.id,
                                                        procedure.id,
                                                        editingProcedureName
                                                      );
                                                    }
                                                    if (e.key === 'Escape') {
                                                      setEditingProcedureId(null);
                                                      setEditingProcedureName('');
                                                    }
                                                  }}
                                                  className="min-w-[8rem] rounded border border-gray-300 px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                                  autoFocus
                                                  aria-label={t('projectDetail.procedureName')}
                                                />
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleSaveProcedureName(
                                                      extraction.id,
                                                      procedure.id,
                                                      editingProcedureName
                                                    )
                                                  }
                                                  className="p-1 text-green-600 hover:bg-green-50 hover:text-green-700 rounded transition-colors"
                                                  title={t('common.save')}
                                                  aria-label={t('common.save')}
                                                >
                                                  <Check size={14} />
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    setEditingProcedureId(null);
                                                    setEditingProcedureName('');
                                                  }}
                                                  className="p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 rounded transition-colors"
                                                  title={t('common.cancel')}
                                                  aria-label={t('common.cancel')}
                                                >
                                                  <X size={14} />
                                                </button>
                                              </div>
                                            ) : (
                                              <div className="flex items-center gap-2 min-w-0">
                                                <span className="min-w-0 break-words hyphens-auto">
                                                  {procedure.procedure_name ?? procedure.assembly.assembly_id}
                                                </span>
                                                <button
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingProcedureId(procedure.id);
                                                    setEditingProcedureName(
                                                      procedure.procedure_name ?? procedure.assembly.assembly_id ?? ''
                                                    );
                                                  }}
                                                  className="p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 rounded transition-colors"
                                                  title={t('projectDetail.editProcedureName')}
                                                  aria-label={t('projectDetail.editProcedureName')}
                                                >
                                                  <Pencil size={14} />
                                                </button>
                                              </div>
                                            )}
                                          </td>
                                          <td
                                            className={`px-4 text-gray-700 tabular-nums ${procedureIndex === 0 ? 'pt-0.5 pb-1.5' : 'py-1.5'}`}
                                          >
                                            {procedure.assembly.tool?.tool_number_unique}
                                          </td>
                                          <td
                                            className={`px-4 text-gray-700 tabular-nums ${procedureIndex === 0 ? 'pt-0.5 pb-1.5' : 'py-1.5'}`}
                                          >
                                            {procedure.assembly.tool?.tool_diameter.toFixed(2)}
                                          </td>
                                          <td
                                            className={`px-4 text-gray-700 tabular-nums ${procedureIndex === 0 ? 'pt-0.5 pb-1.5' : 'py-1.5'}`}
                                          >
                                            {procedure.total_depth.toFixed(2)}
                                          </td>
                                          <td
                                            className={`px-4 text-gray-700 break-words hyphens-auto ${procedureIndex === 0 ? 'pt-0.5 pb-1.5' : 'py-1.5'}`}
                                          >
                                            {procedure.mo_type}
                                          </td>
                                          <td
                                            className={`px-4 text-gray-700 tabular-nums ${procedureIndex === 0 ? 'pt-0.5 pb-1.5' : 'py-1.5'}`}
                                          >
                                            {procedure.spindle_speed_rpm.toFixed(0)}
                                          </td>
                                        </tr>
                                      ))}
                                </Fragment>
                              );
                            })}
                            {/* Empty rows to maintain fixed table height */}
                            {emptyRows.map((emptyRow) => (
                              <tr key={emptyRow.id} className="border-b border-gray-300 bg-gray-50/20">
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400">
                                  <span className="inline-block w-20 h-20" />
                                </td>
                                <td className="py-2 px-4 text-gray-400" />
                                <td className="py-2 px-4 text-gray-400" colSpan={6} />
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4 px-4">
                          <div className="text-sm text-gray-600 whitespace-nowrap">
                            {t('common.showing') || 'Showing'} {startIndex + 1} -{' '}
                            {Math.min(endIndex, sortedHoles.length)} {t('common.of') || 'of'} {sortedHoles.length}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handlePageChange(page - 1)}
                              disabled={page === 0}
                              className={`px-3 py-1 text-sm rounded border transition-colors ${
                                page === 0
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                                  : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                              }`}
                              aria-label={t('common.previousPage') || 'Previous page'}
                            >
                              <ChevronLeft size={16} className="inline" />
                            </button>

                            <div className="flex items-center gap-1">
                              {Array.from({ length: totalPages }, (_, i) => i).map((pageNum) => {
                                // Show first page, last page, current page, and pages around current
                                const showPage =
                                  pageNum === 0 ||
                                  pageNum === totalPages - 1 ||
                                  (pageNum >= page - 1 && pageNum <= page + 1);

                                if (!showPage && pageNum === 1 && page > 2) {
                                  return (
                                    <span key={pageNum} className="px-2 text-gray-400">
                                      ...
                                    </span>
                                  );
                                }
                                if (!showPage && pageNum === totalPages - 2 && page < totalPages - 3) {
                                  return (
                                    <span key={pageNum} className="px-2 text-gray-400">
                                      ...
                                    </span>
                                  );
                                }
                                if (!showPage) return null;

                                return (
                                  <button
                                    key={pageNum}
                                    type="button"
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`px-3 py-1 text-sm rounded border transition-colors ${
                                      pageNum === page
                                        ? 'bg-blue-500 text-white border-blue-500'
                                        : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                                    }`}
                                    aria-label={`${t('common.page') || 'Page'} ${pageNum + 1}`}
                                  >
                                    {pageNum + 1}
                                  </button>
                                );
                              })}
                            </div>

                            <button
                              type="button"
                              onClick={() => handlePageChange(page + 1)}
                              disabled={page >= totalPages - 1}
                              className={`px-3 py-1 text-sm rounded border transition-colors ${
                                page >= totalPages - 1
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                                  : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                              }`}
                              aria-label={t('common.nextPage') || 'Next page'}
                            >
                              <ChevronRight size={16} className="inline" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </TabPanel>
              );
            })}
          </TabView>
          {/* View mode toggle: holes vs procedures + download zip – only when task is complete */}
          {isToggleAvailable && (
            <div className="absolute right-6 top-[7.5px] h-8 flex items-center gap-3 bg-white/80">
              <span className="text-xs text-gray-500 whitespace-nowrap">{t('projectDetail.viewBy')}</span>
              <div className="flex items-center rounded-md border border-gray-300 p-0.5 bg-gray-50/80">
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('holes');
                    const extractionId = sortedDetections[activeTabIndex]?.id;
                    if (extractionId) setCurrentPage((prev) => ({ ...prev, [extractionId]: 0 }));
                  }}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                    viewMode === 'holes'
                      ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title={t('projectDetail.viewByHoles')}
                >
                  <Layers size={12} aria-hidden />
                  <span>{t('projectDetail.holes')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('procedures');
                    const extractionId = sortedDetections[activeTabIndex]?.id;
                    if (extractionId) setCurrentPage((prev) => ({ ...prev, [extractionId]: 0 }));
                  }}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                    viewMode === 'procedures'
                      ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title={t('projectDetail.viewByProcedures')}
                >
                  <Sparkles size={12} aria-hidden />
                  <span>{t('projectDetail.procedures')}</span>
                </button>
              </div>
              <Button
                label={t('projectDetail.downloadAptZip')}
                icon={<Download size={16} className="mr-2" />}
                onClick={handleDownloadAptSourcesZip}
                loading={isDownloadingZip}
                disabled={isDownloadingZip}
                outlined
                size="small"
                tooltip={
                  t('projectDetail.downloadProjectAptSources') ||
                  'Stáhnout všechny H soubory, APT soubory a pplog pro díry'
                }
                tooltipOptions={{ position: 'top' }}
                aria-label={
                  t('projectDetail.downloadProjectAptSources') ||
                  'Stáhnout všechny H soubory, APT soubory a pplog pro díry'
                }
              />
            </div>
          )}
        </div>
      )}

      {/* Preview Dialog – hole or procedure */}
      <ImagePreviewDialog
        visible={previewHoleId !== null || previewProcedureId !== null}
        title={
          previewHoleId !== null
            ? t('projectDetail.holePreview') || `Hole Preview ${previewHoleId}`
            : t('projectDetail.view3DPreview') || 'Zobrazit 3D náhled'
        }
        imageUrl={
          previewHoleId !== null
            ? previewImageUrl
            : previewProcedureId
              ? (previewProcedureThumbnails[previewProcedureId] ?? null)
              : null
        }
        error={
          (previewHoleId !== null && previewImageUrl === null) ||
          (previewProcedureId !== null && !previewProcedureThumbnails[previewProcedureId])
        }
        onClose={handleClosePreview}
      />

      {/* APT Source / H file Dialog */}
      {aptSourceContent && aptSourceHoleId && (
        <TextViewDialog
          visible={showAPTSourceDialog}
          title={aptSourceFilename ?? `APT Source: hole_${aptSourceHoleId}.aptsource`}
          textContent={aptSourceContent}
          onClose={() => {
            setShowAPTSourceDialog(false);
            setAptSourceContent('');
            setAptSourceHoleId(null);
            setAptSourceFilename(null);
          }}
          downloadFilename={aptSourceFilename ?? `hole_${aptSourceHoleId}.aptsource`}
        />
      )}
    </div>
  );
};
